import { ampli } from '@/ampli';
import { useEffect, useState, type ReactNode } from 'react';

import { InlineAlertBox } from '@/components/TransactionModule/Alerts/InlineAlertBox';
import { PlaceOrderButton } from '@/components/TransactionModule/PlaceOrderButton';

import { NATIVE_TOKEN_SYMBOL_PER_CHAIN, solana } from '@/constants/chain';

import { useIsSmartWallet } from '@/hooks/useIsSmartWallet';
import { useMatchaWallets } from '@/hooks/useMatchaWallets';
import { useWalletConnectionModal } from '@/hooks/useWalletConnectionModal';

import { buttons } from '@/styles/buttons';
import { linkStyles } from '@/styles/links';

import { LoadingSpinnerPrimary } from '@/ui/Icons/Animated/LoadingSpinner';
import { ExternalLink } from '@/ui/Link/ExternalLink';

import { PRICE_IMPACT_THRESHOLDS } from '@/utils/errors';
import type { IntentsSubmitApproval } from '@/utils/intents/types';
import type { EthereumAddress } from '@/utils/models';
import { routes } from '@/utils/routes';

import { useModuleContext } from '../ModuleContext';
import type { TradeQuote } from '../tradeQuote';
import { useApproveSellToken, useTradeExecutor } from '../useTradeExecutor';
import type { ValidationResult } from '../useValidation';

/** Inputs for the shared market/limit CTA decision ladder. */
type TradeCtaProps = {
  validationResults: readonly ValidationResult[];
  quote: TradeQuote;
  allowanceTarget?: EthereumAddress;
};

const SIGNED_APPROVAL_BUFFER_SECONDS = 60;

function blockingLabel(result: ValidationResult, chainId: number | undefined): string {
  switch (result.code) {
    case 'native-token-unsupported':
      return `${chainId == null ? '' : NATIVE_TOKEN_SYMBOL_PER_CHAIN[chainId]} not supported`;
    case 'superstate-token-unsupported':
      return 'Superstate not supported';
    case 'token-decimals-unavailable':
      return 'Token decimals unavailable';
    case 'insufficient-balance':
      return 'Insufficient balance';
    case 'insufficient-fee-balance':
      return `Insufficient ${chainId == null ? '' : NATIVE_TOKEN_SYMBOL_PER_CHAIN[chainId]} balance for fees`;
    case 'advanced-order-type-unsupported':
      return 'Not available';
    case 'limit-rate-invalid':
      return 'Invalid limit price';
    default:
      return 'Place order';
  }
}

/** Renders the wallet, validation, approval, and submission ladder shared by market and limit trades. */
export function TradeCta({ validationResults, quote, allowanceTarget }: TradeCtaProps) {
  const { sellToken, buyToken } = useModuleContext();
  const { ethereumWallet, solanaWallet } = useMatchaWallets();
  const { startConnectFlow } = useWalletConnectionModal();
  const chainId = sellToken?.chainId ?? buyToken?.chainId;
  const isSmartWallet = useIsSmartWallet({ chainId });
  const approval = useApproveSellToken({ allowanceTarget, sellToken });
  const { submit, isSubmitting, signIntentsApproval } = useTradeExecutor({
    sellToken,
    buyToken,
  });
  const [signedApproval, setSignedApproval] = useState<IntentsSubmitApproval>();
  const [didAnimateTokenApproval, setDidAnimateTokenApproval] = useState(false);

  useEffect(() => {
    setSignedApproval(undefined);
    setDidAnimateTokenApproval(false);
  }, [sellToken?.address, sellToken?.chainId]);

  useEffect(() => {
    if (!signedApproval) return;
    const message = signedApproval.eip712.message as { deadline?: string; expiry?: string };
    const expirySeconds = Number.parseInt((message.deadline ?? message.expiry) as string, 10);
    if (Number.isNaN(expirySeconds)) return;
    const msUntilReset = (expirySeconds - SIGNED_APPROVAL_BUFFER_SECONDS) * 1000 - Date.now();
    if (msUntilReset <= 0) {
      setSignedApproval(undefined);
      return;
    }
    const timer = window.setTimeout(() => setSignedApproval(undefined), msUntilReset);
    return () => window.clearTimeout(timer);
  }, [signedApproval]);

  const connectWallet = (
    walletChainType?: 'ethereum-and-solana' | 'ethereum-only' | 'solana-only',
  ) => {
    ampli.walletConnectClicked({ source: 'trade_module', ui_location: 'trade_module' });
    startConnectFlow({ source: 'trade_module', walletChainType });
  };

  const hasNoTokensSelected = !sellToken && !buyToken;
  if (hasNoTokensSelected && !ethereumWallet && !solanaWallet) {
    return <ActionButton onClick={() => connectWallet()}>Connect wallet</ActionButton>;
  }
  if (chainId !== solana.id && !ethereumWallet) {
    return (
      <ActionButton onClick={() => connectWallet('ethereum-only')}>Connect EVM wallet</ActionButton>
    );
  }
  if (chainId === solana.id && !solanaWallet) {
    return (
      <ActionButton onClick={() => connectWallet('solana-only')}>
        Connect Solana wallet
      </ActionButton>
    );
  }

  const blockingResult = validationResults.find((result) => result.severity === 'blocking');
  const needsTokenApproval =
    blockingResult?.code === 'token-needs-approval' ||
    blockingResult?.code === 'token-needs-approval-increase';

  if (blockingResult && !needsTokenApproval) {
    return <ActionButton disabled>{blockingLabel(blockingResult, chainId)}</ActionButton>;
  }

  if (needsTokenApproval) {
    return (
      <div className="flex flex-col items-center gap-4 w-full">
        <button
          data-testid="trade-approval-button"
          className={buttons({ size: 'large', color: 'black', shape: 'pill' })}
          disabled={approval.isPendingApproval || approval.isApproving}
          onClick={() => void approval.approve()}
          type="button"
        >
          {approval.isPendingApproval ? (
            <>
              <LoadingSpinnerPrimary className="mr-2.5 will-change-transform" />
              Approve in your wallet
            </>
          ) : approval.isApproving ? (
            <>
              <LoadingSpinnerPrimary className="mr-2.5 will-change-transform" />
              Approving...
            </>
          ) : approval.needsAllowanceReset ? (
            <>Reset allowance for {sellToken?.symbol}</>
          ) : (
            <>Approve spending {sellToken?.symbol}</>
          )}
        </button>
        {approval.needsAllowanceReset ? (
          <InlineAlertBox
            title="Allowance reset required"
            description="USDT requires resetting your allowance to 0 before changing it."
          />
        ) : null}
        <ExternalLink
          className={linkStyles({
            color: 'purple',
            font: 'LABEL_MD',
            underline: true,
            class: 'inline-flex mx-auto',
          })}
          href={routes.HELP_APPROVE_TOKEN}
        >
          Why do I have to approve a token?
        </ExternalLink>
      </div>
    );
  }

  const rawIntentsApproval =
    quote.source === 'intents' && quote.raw && 'approval' in quote.raw
      ? quote.raw.approval
      : undefined;
  const needsIntentsApproval = Boolean(rawIntentsApproval && !signedApproval && !isSmartWallet);
  const hasPriceImpact = Boolean(
    quote.priceImpact && quote.priceImpact.percent + PRICE_IMPACT_THRESHOLDS[0] <= 0,
  );

  const handleSignIntentsApproval = async () => {
    const outcome = await signIntentsApproval();
    if (outcome.status === 'signed') setSignedApproval(outcome.approval);
  };

  // A `fetched` market quote can still be a `/price` payload, which no lane can execute.
  const hasExecutablePayload =
    quote.raw !== undefined &&
    ('transaction' in quote.raw || ('trade' in quote.raw && quote.raw.trade !== null));
  const hasSubmittableQuote =
    quote.source === 'advanced'
      ? quote.status === 'derived'
      : quote.status === 'fetched' && hasExecutablePayload;
  if (!hasSubmittableQuote && quote.status !== 'loading') {
    return <ActionButton disabled>Place order</ActionButton>;
  }

  return (
    <PlaceOrderButton
      onClickApprove={() => void handleSignIntentsApproval()}
      onSubmit={() => void submit({ signedApproval })}
      hasPriceImpact={hasPriceImpact}
      isLoading={quote.status === 'loading' || isSubmitting}
      needsApproval={needsIntentsApproval}
      needsWalletConfirmation={isSubmitting}
      sellToken={sellToken}
      buyToken={buyToken}
      sellTokenSymbol={sellToken?.symbol}
      tokenApproval={{
        isTokenApproved: Boolean(signedApproval),
        didAnimateTokenApproval,
        setDidAnimateTokenApproval,
      }}
    />
  );
}

/** Full-width pill CTA shared by the trade panels' wallet and validation states. */
export function ActionButton({
  children,
  disabled,
  onClick,
}: {
  children: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      className={buttons({ size: 'large', shape: 'pill', color: 'black' })}
      disabled={disabled}
      onClick={onClick}
      type="button"
    >
      {children}
    </button>
  );
}
